'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navItems = [
  { label: 'Dashboard', href: '/dashboard', icon: 'SquaresPlusIcon' },
  { label: 'Kanban Board', href: '/kanban-board', icon: 'ViewColumnsIcon' },
  { label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon' },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-border transition-all duration-300 ease-in-out ${
          sidebarExpanded ? 'w-60' : 'w-16'
        } flex-shrink-0`}
        style={{ background: 'var(--muted)' }}
      >
        {/* Logo area */}
        <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[64px]">
          <div className="flex-shrink-0">
            <AppLogo size={32} />
          </div>
          {sidebarExpanded && (
            <span className="font-semibold text-foreground text-base tracking-tight truncate">
              TaskFlow
            </span>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-2 py-4 flex flex-col gap-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href || (item.href === '/dashboard' && pathname === '/');
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                title={!sidebarExpanded ? item.label : undefined}
                className={isActive ? 'nav-item-active' : 'nav-item'}
              >
                <Icon name={item.icon as any} size={20} />
                {sidebarExpanded && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom: collapse toggle + user */}
        <div className="px-2 py-4 border-t border-border flex flex-col gap-2">
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="nav-item w-full"
            aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <Icon
              name={sidebarExpanded ? 'ChevronLeftIcon' : 'ChevronRightIcon'}
              size={18}
            />
            {sidebarExpanded && <span className="text-xs">Collapse</span>}
          </button>
          <Link href="/settings" className={pathname === '/settings' ? 'nav-item-active' : 'nav-item'}>
            <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center text-primary text-xs font-semibold flex-shrink-0">
              AM
            </div>
            {sidebarExpanded && (
              <span className="text-xs truncate">Alex Morgan</span>
            )}
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0" style={{ background: 'var(--muted)' }}>
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-foreground text-sm">TaskFlow</span>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/settings" className="p-2 rounded-lg hover:bg-secondary transition-colors">
              <Icon name="UserCircleIcon" size={22} className="text-secondary-foreground" />
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="fade-in">
            {children}
          </div>
        </main>

        {/* Mobile bottom tab bar */}
        <nav className="lg:hidden flex items-center border-t border-border flex-shrink-0" style={{ background: 'var(--muted)' }}>
          {navItems.map((item) => {
            const isActive = pathname === item.href || (item.href === '/dashboard' && pathname === '/');
            return (
              <Link
                key={`bottom-nav-${item.href}`}
                href={item.href}
                className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 min-h-[56px] transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <Icon name={item.icon as any} size={22} />
                <span className="text-[10px] font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}